import type { ReactNode } from 'react'
import { Info, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface StatCardProps {
  icon: LucideIcon
  /** Color del icono; el resto de la tarjeta usa los tokens del dashboard. */
  iconClassName?: string
  label: string
  /** `null`/`undefined` muestran el estado sin datos (—), nunca un 0 inventado. */
  value?: ReactNode
  unit?: string
  detail?: ReactNode
  delta?: ReactNode
  /** Fórmula y período de la métrica, visible al enfocar o pasar el cursor. */
  formula?: string
  /** Línea de tendencia u otro complemento bajo el valor. */
  trend?: ReactNode
  badge?: ReactNode
  className?: string
}

/** KPI del dashboard: icono, etiqueta, valor, unidad y detalle secundario. */
export function StatCard({
  icon: Icon,
  iconClassName = 'text-brand',
  label,
  value,
  unit,
  detail,
  delta,
  formula,
  trend,
  badge,
  className,
}: StatCardProps) {
  const hasData = value !== null && value !== undefined

  return (
    <div className={cn('rounded-xl border border-slate-100 bg-white p-4 shadow-sm', className)}>
      <div className="mb-2 flex items-center justify-between">
        <Icon className={cn('h-5 w-5', iconClassName)} aria-hidden="true" />
        {badge}
      </div>
      <div className="flex items-center gap-1">
        <p className="text-xs font-semibold text-slate-500">{label}</p>
        {formula && (
          <span className="group relative inline-flex">
            <button
              type="button"
              aria-label={`Fórmula: ${formula}`}
              className="inline-flex rounded-full text-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <span
              role="tooltip"
              className="invisible absolute bottom-full left-1/2 z-20 mb-1 w-36 -translate-x-1/2 rounded-lg bg-slate-900 px-2.5 py-1.5 text-left text-[11px] font-normal leading-snug text-white opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 sm:w-56"
            >
              {formula}
            </span>
          </span>
        )}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        {hasData ? (
          <p className="text-2xl font-bold tracking-tight text-slate-900">
            {value}
            {unit && <span className="text-sm font-semibold">{unit}</span>}
          </p>
        ) : (
          <p className="text-2xl font-bold tracking-tight text-slate-400" aria-label="Sin datos">—</p>
        )}
      </div>
      {trend}
      {detail && <p className="mt-1 text-xs font-medium text-slate-400">{detail}</p>}
      {delta && <p className="mt-0.5 text-xs text-slate-400">{delta}</p>}
    </div>
  )
}
